import { addDays, isIsoDate, nextBusinessDay } from "./dates";
import { DomainError } from "./errors";

export const MAX_INSTALLMENTS = 60;
export const MAX_TERM_DAYS = 3650;

/** Condição de pagamento: prazos em dias a partir da data base e entrada à vista (vence na data base). */
export type PaymentTermRule = { days: readonly number[]; downPayment: boolean };
export type Installment = { number: number; dueDate: string; amount: number };

/** Primeiro problema dos prazos (mensagem para a UI) ou null. */
export function termProblem(term: PaymentTermRule): string | null {
  const { days, downPayment } = term;
  if (days.length === 0 && !downPayment) return "Informe ao menos um prazo";
  if (days.some((d) => !Number.isSafeInteger(d) || d < 0 || d > MAX_TERM_DAYS))
    return `Cada prazo deve ser um número inteiro de 0 a ${MAX_TERM_DAYS} dias`;
  if (days.some((d, i) => i > 0 && d <= days[i - 1]!)) return "Os prazos devem ser crescentes";
  if (downPayment && days[0] === 0) return "Com entrada, o primeiro prazo deve ser maior que zero";
  if (days.length + (downPayment ? 1 : 0) > MAX_INSTALLMENTS) return `No máximo ${MAX_INSTALLMENTS} parcelas`;
  return null;
}

/**
 * Parcelas (05 §4): n = prazos (+1 com entrada); `amount = floor(total / n)` e o resto na **última**;
 * `dueDate = baseDate + days[i]` (a entrada vence na data base). Com `postponeToBusinessDay` o vencimento que cai
 * em sábado, domingo ou feriado nacional vai para o próximo dia útil.
 */
export function buildInstallments(
  total: number,
  term: PaymentTermRule,
  baseDate: string,
  opts: { postponeToBusinessDay?: boolean } = {},
): Installment[] {
  if (!Number.isSafeInteger(total) || total < 0)
    throw new DomainError("invalid_money", `total inválido: ${total}`);
  const problem = termProblem(term);
  if (problem) throw new DomainError("invalid_term", problem);
  if (!isIsoDate(baseDate)) throw new DomainError("invalid_date", `data inválida: ${baseDate}`);
  const offsets = term.downPayment ? [0, ...term.days] : [...term.days];
  const n = offsets.length;
  const amount = Math.floor(total / n);
  const rest = total - amount * n;
  return offsets.map((days, i) => {
    const raw = addDays(baseDate, days);
    return {
      number: i + 1,
      dueDate: opts.postponeToBusinessDay ? nextBusinessDay(raw) : raw,
      amount: i === n - 1 ? amount + rest : amount,
    };
  });
}

/** "30 60 90", "30/60/90", "30,60,90" → [30, 60, 90]. Só inteiros não negativos; a ordem é checada por termProblem. */
export function parseTermDays(text: string): number[] {
  const tokens = text.split(/[\s/,;]+/).filter(Boolean);
  if (tokens.some((t) => !/^\d{1,5}$/.test(t)))
    throw new DomainError(
      "invalid_term",
      "Use números de dias separados por espaço ou barra (ex.: 30/60/90)",
    );
  return tokens.map(Number);
}

/** Texto curto da condição: "À vista", "30/60/90 dias", "Entrada + 30 dias". */
export function describeTerm(term: PaymentTermRule): string {
  const { days, downPayment } = term;
  if (days.length === 0 || (days.length === 1 && days[0] === 0 && !downPayment)) return "À vista";
  const list = `${days.join("/")} dias`;
  return downPayment ? `Entrada + ${list}` : list;
}
